import { Fragment, type ComponentType } from "react";

import { SectionRenderer } from "@/components/cms/SectionRenderer";
import type { SectionDoc } from "@/content";
import { usePreviewMode } from "@/lib/preview-mode";
import { useOrderedSections } from "@/page-data";

/**
 * A page, as the sections an editor arranged.
 *
 * Each route hands this the components for the sections that have a layout
 * written for them, keyed by the section's key ("home.hero") or, for pages that
 * share a shape, by the part after the page name ("*.introduction"). Anything
 * else, which includes every section somebody added in the panel, is drawn by
 * the generic renderer from its type. The order is the saved order: move a
 * section in the page editor and it moves here.
 */

export type SectionProps = { section: SectionDoc };
export type SectionRenderers = Record<string, ComponentType<SectionProps>>;

/** "section-hero" for home.hero; "section-<id>" for a section added in the panel. */
export const sectionAnchor = (section: { _id?: string; key?: string }) =>
  `section-${section.key && section.key.includes(".") ? section.key.slice(section.key.indexOf(".") + 1) : String(section._id ?? "")}`;

export function rendererFor(renderers: SectionRenderers, key?: string) {
  if (!key) return undefined;
  if (renderers[key]) return renderers[key];
  const dot = key.indexOf(".");
  return dot > 0 ? renderers[`*${key.slice(dot)}`] : undefined;
}

export function PageSections({ renderers }: { renderers: SectionRenderers }) {
  const sections = useOrderedSections();
  const preview = usePreviewMode();

  return (
    <>
      {sections.map((section, index) => {
        const Renderer = rendererFor(renderers, section.key);
        const content = Renderer ? <Renderer section={section} /> : <SectionRenderer section={section} />;
        const id = String(section._id ?? section.key ?? `${section.type}-${index}`);
        // In the panel's preview, a marker the frame scrolls to. `contents`
        // keeps it out of the layout, so the preview matches the site.
        if (preview) {
          return (
            <div key={id} data-cms-section={id} className="contents">
              {content}
            </div>
          );
        }
        /*
         * An address for each section, so "See it on the website" in the panel
         * opens the page at the section being edited: /#section-where-we-work.
         * Empty and zero-height, so it changes nothing about the layout.
         */
        return (
          <Fragment key={id}>
            <span id={sectionAnchor(section)} aria-hidden="true" className="block h-0 scroll-mt-24" />
            {content}
          </Fragment>
        );
      })}
    </>
  );
}
